import React, { useState, useRef, useEffect } from 'react';
import { updateGame } from '../../services/sessionService';
import Button from '../common/Button';
import { Modal, ModalContent, ModalCloseButton } from '../common';
import {
  DEFAULT_SCORING,
  validateGameScore,
  shouldShowWinQuickButton,
  applyWinQuickScore,
} from '../../utils/gameScoreRules';
import { handleMobileScoreFocus, markSkipMobileScoreClear, mobileScoreInputAttrs } from '../../utils/mobileScoreInput';

/**
 * Edit Game Modal Component
 * Allows editing scores for a logged game
 */
const EditGameModal = ({
  game,
  playersById,
  currentUserId,
  onClose,
  onGameUpdated,
  scoringConfig = DEFAULT_SCORING,
}) => {
  const [team1Score, setTeam1Score] = useState(game.team_1_score?.toString() || '');
  const [team2Score, setTeam2Score] = useState(game.team_2_score?.toString() || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [scoreError, setScoreError] = useState('');
  const team1ScoreInputRef = useRef(null);
  const team2ScoreInputRef = useRef(null);

  const { pointsTarget } = scoringConfig;

  // Validate scores whenever they change
  useEffect(() => {
    if (team1Score !== '' && team2Score !== '') {
      const validationError = validateGameScore(team1Score, team2Score, scoringConfig);
      setScoreError(validationError || '');
    } else {
      setScoreError('');
    }
  }, [team1Score, team2Score, scoringConfig]);

  const handleScoreChange = (team, value) => {
    if (value === '' || /^\d+$/.test(value)) {
      if (team === 'team1') {
        setTeam1Score(value);
        if (value.length === 2 && team2ScoreInputRef.current) {
          markSkipMobileScoreClear('team2');
          team2ScoreInputRef.current.focus();
        }
      } else {
        setTeam2Score(value);
      }
      setError(null);
      setScoreError('');
    }
  };

  const handleScoreIncrement = (team, delta) => {
    const currentScore = team === 'team1' ? team1Score : team2Score;
    const newScore = Math.max(0, Math.min(99, (parseInt(currentScore) || 0) + delta));
    const newScoreStr = newScore === 0 ? '' : newScore.toString();
    
    if (team === 'team1') {
      setTeam1Score(newScoreStr);
    } else {
      setTeam2Score(newScoreStr);
    }
    setError(null);
    setScoreError('');
  };

  const showWinQuickButtons = shouldShowWinQuickButton(team1Score, team2Score, pointsTarget);
  const handleWinButton = (team) =>
    applyWinQuickScore(team, pointsTarget, {
      setTeam1Score,
      setTeam2Score,
      team1InputRef: team1ScoreInputRef,
      team2InputRef: team2ScoreInputRef,
      onClearErrors: () => {
        setError(null);
        setScoreError('');
      },
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const score1 = parseInt(team1Score);
    const score2 = parseInt(team2Score);
    
    if (isNaN(score1) || isNaN(score2) || score1 < 0 || score2 < 0) {
      setError('Please enter valid scores');
      return;
    }

    if (score1 === score2) {
      setError('Scores cannot be equal');
      return;
    }

    const validationError = validateGameScore(team1Score, team2Score, scoringConfig);
    if (validationError) {
      setError(validationError);
      setScoreError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await updateGame(game.id, {
        team_1_score: score1,
        team_2_score: score2
      }, playersById);

      if (onGameUpdated) {
        onGameUpdated();
      }
      onClose();
    } catch (err) {
      console.error('Error updating game:', err);
      setError(err.message || 'Failed to update game. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get team players for display
  const getTeamPlayers = (teamNumber) => {
    if (!game.players_session_games) return [];
    return game.players_session_games
      .filter(psg => psg.team_number === teamNumber)
      .map(psg => playersById[psg.player_id] || psg.players)
      .filter(Boolean);
  };

  const team1Players = getTeamPlayers(1);
  const team2Players = getTeamPlayers(2);

  return (
    <Modal isOpen={!!game} onClose={onClose} closeOnBackdropClick={!isSubmitting}>
      <ModalContent size="medium" className="overflow-y-auto">
        <div className="flex items-center justify-between mb-6 pr-8">
          <h3 className="text-xl font-semibold text-text-primary">Edit Game</h3>
          <ModalCloseButton onClose={onClose} />
        </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Teams Display */}
            <div className="flex flex-col md:flex-row gap-6">
              {/* Team 1 Panel */}
              <div className="flex-1 p-4 rounded-lg bg-team1-light ring-1 ring-team1">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-semibold text-team1">Team 1</h4>
                </div>
                <div className="space-y-2">
                  {team1Players.map((player, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="text-text-secondary">
                        {player.first_name} {player.last_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team 2 Panel */}
              <div className="flex-1 p-4 rounded-lg bg-team2-light ring-1 ring-team2">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-semibold text-team2">Team 2</h4>
                </div>
                <div className="space-y-2">
                  {team2Players.map((player, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="text-text-secondary">
                        {player.first_name} {player.last_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Score Input */}
            <div className="w-full p-3 bg-gradient-to-r from-team1-light to-team2-light rounded-lg border border-border">
              <div className="flex items-center justify-between gap-4">
                {/* Team 1 Score Section */}
                <div className="flex flex-col items-center gap-1.5 flex-1">
                  <span className="text-xs font-medium text-team1">Team 1</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleScoreIncrement('team1', -1)}
                      className="hidden sm:flex w-8 h-8 items-center justify-center bg-team1-light hover:bg-team1/30 text-team1 rounded-md border border-team1 font-bold text-lg transition-colors"
                      aria-label="Decrease Team 1 score"
                    >
                      −
                    </button>
                    
                    <input
                      ref={team1ScoreInputRef}
                      placeholder="0"
                      value={team1Score}
                      onChange={(e) => handleScoreChange('team1', e.target.value)}
                      onFocus={(e) => handleMobileScoreFocus(e, 'team1', () => setTeam1Score(''))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !error && !scoreError && team1Score && team2Score) {
                          handleSubmit(e);
                        }
                      }}
                      maxLength={2}
                      className="w-16 h-8 px-2 border-2 border-team1 rounded-md bg-surface shadow-sm focus:outline-none focus:ring-2 focus:ring-team1 focus:border-team1 text-center text-lg font-bold text-team1"
                      {...mobileScoreInputAttrs}
                    />
                    
                    <button
                      type="button"
                      onClick={() => handleScoreIncrement('team1', 1)}
                      className="hidden sm:flex w-8 h-8 items-center justify-center bg-team1-light hover:bg-team1/30 text-team1 rounded-md border border-team1 font-bold text-lg transition-colors"
                      aria-label="Increase Team 1 score"
                    >
                      +
                    </button>
                  </div>
                  
                  {showWinQuickButtons && (
                  <button
                    type="button"
                    onClick={() => handleWinButton('team1')}
                    className="px-4 py-1.5 text-xs font-semibold bg-team1 hover:bg-team1/80 text-white rounded-md transition-colors shadow-sm"
                    aria-label={`Set Team 1 score to ${pointsTarget}`}
                  >
                    Win ({pointsTarget})
                  </button>
                  )}
                </div>
                
                {/* VS Divider */}
                <div className="flex flex-col items-center gap-0.5">
                  <div className="w-6 h-0.5 bg-border-medium" />
                  <span className="text-sm font-bold text-text-secondary">VS</span>
                  <div className="w-6 h-0.5 bg-border-medium" />
                </div>
                
                {/* Team 2 Score Section */}
                <div className="flex flex-col items-center gap-1.5 flex-1">
                  <span className="text-xs font-medium text-team2">Team 2</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleScoreIncrement('team2', -1)}
                      className="hidden sm:flex w-8 h-8 items-center justify-center bg-team2-light hover:bg-team2/30 text-team2 rounded-md border border-team2 font-bold text-lg transition-colors"
                      aria-label="Decrease Team 2 score"
                    >
                      −
                    </button>
                    
                    <input
                      ref={team2ScoreInputRef}
                      placeholder="0"
                      value={team2Score}
                      onChange={(e) => handleScoreChange('team2', e.target.value)}
                      onFocus={(e) => handleMobileScoreFocus(e, 'team2', () => setTeam2Score(''))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !error && !scoreError && team1Score && team2Score) {
                          handleSubmit(e);
                        }
                      }}
                      maxLength={2}
                      className="w-16 h-8 px-2 border-2 border-team2 rounded-md bg-surface shadow-sm focus:outline-none focus:ring-2 focus:ring-team2 focus:border-team2 text-center text-lg font-bold text-team2"
                      {...mobileScoreInputAttrs}
                    />
                    
                    <button
                      type="button"
                      onClick={() => handleScoreIncrement('team2', 1)}
                      className="hidden sm:flex w-8 h-8 items-center justify-center bg-team2-light hover:bg-team2/30 text-team2 rounded-md border border-team2 font-bold text-lg transition-colors"
                      aria-label="Increase Team 2 score"
                    >
                      +
                    </button>
                  </div>
                  
                  {showWinQuickButtons && (
                  <button
                    type="button"
                    onClick={() => handleWinButton('team2')}
                    className="px-4 py-1.5 text-xs font-semibold bg-team2 hover:bg-team2/80 text-white rounded-md transition-colors shadow-sm"
                    aria-label={`Set Team 2 score to ${pointsTarget}`}
                  >
                    Win ({pointsTarget})
                  </button>
                  )}
                </div>
              </div>
            </div>

            {/* Score Validation Error */}
            {scoreError && (
              <div className="bg-danger-light border border-danger/30 text-danger-dark px-3 py-2 rounded text-sm text-center">
                {scoreError}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-danger-light border border-danger/30 rounded-lg p-3">
                <p className="text-sm text-danger-dark">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || !!scoreError}
              >
                {isSubmitting ? 'Updating...' : 'Update Game'}
              </Button>
            </div>
          </form>
      </ModalContent>
    </Modal>
  );
};

export default EditGameModal;

